import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { endpoints } from '../../config/api';
import JobCard from '../jobs/JobCard';
import { extractDataFromResponse, processJobsData } from '../../utils/apiUtils';
import './RecentJobs.css';

function RecentJobs({ currentJob, limit = 3, variant = 'default' }) {
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentJobs();
  }, [currentJob]);

  const fetchRecentJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(endpoints.jobs.list, {
        params: {
          limit: 10, // Fetch more to ensure we have enough after filtering
          status: 'active',
          sort: '-createdAt' // Sort by newest first
        }
      });
      
      // Extract and process jobs data using utility function
      const jobsData = extractDataFromResponse(response, 'data');
      const processedJobs = processJobsData(jobsData);

      // Filter out current job if provided
      const filteredJobs = currentJob 
        ? processedJobs.filter(job => job._id !== currentJob._id)
        : processedJobs;

      // Sort jobs by posted date in descending order (newest first)
      const sortedJobs = filteredJobs.sort((a, b) => {
        const dateA = new Date(a.postedDate);
        const dateB = new Date(b.postedDate);
        return dateB - dateA;
      });

      setRecentJobs(sortedJobs.slice(0, limit));
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      setRecentJobs([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    if (variant === 'sidebar') {
      return (
        <div className="recent-jobs-sidebar-compact">
          <div className="loading-skeleton-sidebar">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-card-sidebar" />
            ))}
          </div>
        </div>
      );
    }
    
    return (
      <section className="recent-jobs">
        <div className="container">
          <h2 className="section-title">Recent Job Posts</h2>
          <div className="loading-skeleton">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton-card" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className="recent-jobs-sidebar-compact">
        <div className="jobs-list-sidebar">
          {recentJobs.map(job => (
            <div key={job._id} className="job-item-sidebar">
              <Link to={`/jobs/${job.slug}`} className="job-link-sidebar">
                <div className="job-header-sidebar">
                  <div className="company-logo-sidebar">
                    {job.hiringOrganization?.logo ? (
                      <img 
                        src={job.hiringOrganization.logo} 
                        alt={job.hiringOrganization.name}
                        className="logo-image-sidebar"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="logo-placeholder-sidebar" style={{ display: job.hiringOrganization?.logo ? 'none' : 'flex' }}>
                      {(job.hiringOrganization?.name || job.company || 'C').charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="job-info-sidebar">
                    <h4 className="job-title-sidebar">{job.title}</h4>
                    <p className="company-name-sidebar">{job.hiringOrganization?.name || job.company}</p>
                  </div>
                </div>
                <div className="job-meta-sidebar">
                  <span className="location-sidebar">{job.location?.address || job.location}</span>
                  <span className="date-sidebar">{new Date(job.postedDate).toLocaleDateString()}</span>
                </div>
              </Link>
            </div>
          ))}
        </div>
        <div className="view-all-sidebar">
          <Link to="/search" className="view-all-link-sidebar">
            View All Jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <section className="recent-jobs">
      <div className="container">
        <h2 className="section-title">Recent Job Posts</h2>
        
        <div className="jobs-grid">
          {recentJobs.map(job => (
            <JobCard key={job._id} job={job} />
          ))}
        </div>

        <div className="view-all-container">
          <Link to="/search" className="view-all-jobs">
            View All Jobs
          </Link>
        </div>
      </div>
    </section>
  );
}

export default RecentJobs; 